import React, { useState } from "react";
import {
  Star,
  ShieldCheck,
  Clock,
  MapPin,
  Phone,
  ChevronRight,
  Info,
  CalendarCheck,
  Users,
  Briefcase,
  Fuel,
  Settings,
  ArrowRight,
  Check,
  ChevronDown,
  Plane,
} from "lucide-react";
import Form from "../components/Form";
import { Helmet } from "react-helmet-async";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const formatPrice = (price, currency) => {
  if (!price) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "CAD",
      minimumFractionDigits: 2,
    }).format(price);
  } catch {
    return `${currency || ""} ${price}`;
  }
};

// ── Sub-components ───────────────────────────────────────────────────────────

const PolicyCard = ({ icon, title, value }) => (
  <div className="bg-surface border border-surface-border p-4 rounded-xl flex flex-col justify-center items-center text-center gap-2 shadow-card">
    <div className="text-primary bg-accent p-2 rounded-lg mb-1">{icon}</div>
    <p className="font-heading text-[10px] font-bold text-text-muted uppercase tracking-widest">
      {title}
    </p>
    <p className="font-heading text-xs font-semibold text-text">{value}</p>
  </div>
);

const VehicleCard = ({ car }) => {
  const displayPrice =
    car.minPrice?.formatted ||
    formatPrice(car.minPrice?.amount, car.minPrice?.currency);

  return (
    <div className="group bg-surface rounded-xl border border-surface-border overflow-hidden hover:shadow-card transition-all duration-300 flex flex-col">
      {/* Image */}
      <div className="relative flex items-center justify-center px-8 pt-8 pb-4 aspect-[16/9]">
        <img
          src={car.imageUrl}
          alt={car.example}
          className="max-w-full max-h-28 object-contain scale-110 group-hover:scale-125 transition-transform duration-500"
          onError={(e) => {
            e.target.style.opacity = "0.3";
          }}
        />
        <span className="absolute top-3 left-3 bg-text text-white px-3 py-1 rounded-full text-[10px] font-heading font-semibold uppercase tracking-wider">
          {car.type}
        </span>
      </div>

      {/* Details */}
      <div className="p-5 flex flex-col flex-grow space-y-3">
        <div>
          <h3 className="font-heading text-sm font-semibold text-text leading-snug">
            {car.example}
          </h3>
          <p className="font-body text-xs text-text-muted">or similar class</p>
        </div>

        {/* Specs */}
        <div className="grid grid-cols-2 gap-y-2">
          <div className="flex items-center gap-2 text-xs font-body text-text-muted">
            <Users size={13} className="text-text-muted flex-shrink-0" />
            <span>{car.capacity?.passengers} Seats</span>
          </div>
          <div className="flex items-center gap-2 text-xs font-body text-text-muted">
            <Briefcase size={13} className="text-text-muted flex-shrink-0" />
            <span>{car.capacity?.largeBags} Bags</span>
          </div>
          <div className="flex items-center gap-2 text-xs font-body text-text-muted">
            <Settings size={13} className="text-text-muted flex-shrink-0" />
            <span>Auto</span>
          </div>
          <div className="flex items-center gap-2 text-xs font-body text-text-muted">
            <Fuel size={13} className="text-text-muted flex-shrink-0" />
            <span>Hybrid</span>
          </div>
        </div>

        {/* Features */}
        {car.features?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {car.features.map((feature, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 font-body text-[10px] font-medium text-text-muted bg-surface-raised px-2 py-1 rounded-md border border-surface-border"
              >
                <Check size={9} className="text-primary" /> {feature}
              </span>
            ))}
          </div>
        )}

        {/* Price + CTA */}
        <div className="pt-3 border-t border-surface-border flex items-center justify-between mt-auto">
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary hover:bg-primary-hover text-white font-heading text-xs font-semibold transition-colors duration-200"
          >
            Reserve Now <ArrowRight size={13} />
          </a>
        </div>
      </div>
    </div>
  );
};

const FaqItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="border border-surface-border rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-surface hover:bg-surface-raised transition-colors"
      aria-expanded={isOpen}
    >
      <span className="font-heading font-semibold text-text text-sm">
        {question}
      </span>
      <ChevronDown
        size={16}
        className={cx(
          "flex-shrink-0 text-primary transition-transform duration-200",
          isOpen && "rotate-180",
        )}
      />
    </button>
    {isOpen && (
      <div className="px-5 pb-4 pt-1 bg-surface border-t border-surface-border">
        <p className="font-body text-text-muted text-sm leading-relaxed">
          {answer}
        </p>
      </div>
    )}
  </div>
);

// ── Main Component ───────────────────────────────────────────────────────────

export function VendorLocationView({ data }) {
  const [openFaq, setOpenFaq] = useState(null);

  if (!data) return null;

  const { vendor, location, bookingDetails, vehicleCategories, reviews } = data;
  const logoSrc =
    typeof vendor?.logo === "string" ? vendor.logo : vendor?.logo?.url;

  return (
    <div className="min-h-screen bg-surface-raised font-body">
      <Helmet>
        <title>
          {vendor?.name} at {location?.name} | RentGurus
        </title>
        <meta
          name="description"
          content={`Book ${vendor?.name} car rental at ${location?.name}. Instant confirmation, flexible cancellation.`}
        />
      </Helmet>

      {/* ── HERO ── */}
      <header
        className="px-4 sm:px-6 pt-10 pb-32 relative overflow-hidden mt-12"
        style={{
          background:
            "linear-gradient(135deg, #14222d 0%, #14222d 45%, #405759 60%, #587471 78%, #90bda8 100%)",
        }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            {/* Logo */}
            <div className="w-16 h-16 bg-surface rounded-xl shadow-card flex items-center justify-center p-3 flex-shrink-0">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={vendor?.name}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="font-heading text-text font-bold text-xs text-center">
                  {vendor?.name}
                </span>
              )}
            </div>

            {/* Name + rating + tags */}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3 mt-9">
                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight">
                  {vendor?.name}
                </h1>
                {vendor?.overallRating && (
                  <span className="inline-flex items-center gap-1.5 bg-primary/20 border border-primary/30 text-primary px-3 py-1 rounded-full text-xs font-heading font-semibold">
                    <Star size={11} className="fill-primary" />
                    {vendor.overallRating}/5
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-white/60 text-sm font-body">
                <MapPin size={14} className="flex-shrink-0 text-primary" />
                <span>{location?.name}</span>
              </div>
              {vendor?.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {vendor.tags.map((tag) => (
                    <span
                      key={tag}
                      className="font-heading text-[10px] font-semibold bg-white/10 text-white/70 border border-white/10 px-2.5 py-1 rounded-full uppercase tracking-wider"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── SEARCH FORM ── */}
      <div className="px-4 sm:px-6 -mt-12 relative z-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-surface rounded-xl border border-surface-border p-3">
            <Form />
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8 items-start">
          {/* ── LEFT COLUMN ── */}
          <div className="lg:col-span-8 space-y-8">
            {/* Policy cards */}
            <div className="grid grid-cols-3 gap-3">
              <PolicyCard
                icon={<ShieldCheck size={18} />}
                title="Security Deposit"
                value={
                  bookingDetails?.deposit
                    ? `${bookingDetails.deposit.amount} ${bookingDetails.deposit.currency}`
                    : "—"
                }
              />
              <PolicyCard
                icon={<Clock size={18} />}
                title="Confirmation"
                value="Instant"
              />
              <PolicyCard
                icon={<CalendarCheck size={18} />}
                title="Cancellation"
                value="Flexible"
              />
            </div>

            {/* Vehicles */}
            <section>
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-xl font-bold text-text flex items-center gap-3">
                  <span className="w-1 h-6 bg-primary rounded-full" />
                  Available Vehicles
                </h2>
                <span className="font-heading text-xs font-semibold text-text-muted uppercase tracking-widest">
                  {vehicleCategories?.length} Found
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                {vehicleCategories?.map((car, idx) => (
                  <VehicleCard key={idx} car={car} />
                ))}
              </div>
            </section>

            {/* Reviews */}
            {reviews?.length > 0 && (
              <section className="bg-surface rounded-xl border border-surface-border p-6 shadow-card">
                <h3 className="font-heading text-base font-bold text-text mb-6">
                  Recent Reviews
                </h3>
                <div className="space-y-6">
                  {reviews.map((review, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-dark-deep flex items-center justify-center font-heading font-bold text-white text-sm flex-shrink-0">
                        {review.author?.charAt(0)}
                      </div>
                      <div className="flex-1 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <p className="font-heading text-sm font-semibold text-text">
                            {review.author}
                          </p>
                          <p className="font-body text-xs text-text-muted">
                            {review.date}
                          </p>
                        </div>
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, s) => (
                            <Star
                              key={s}
                              size={12}
                              className={
                                s < review.rating
                                  ? "fill-primary text-primary"
                                  : "text-surface-border"
                              }
                            />
                          ))}
                        </div>
                        <p className="font-body text-sm text-text-muted leading-relaxed italic">
                          "{review.comment}"
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Booking details */}
            {bookingDetails && (
              <section className="bg-surface rounded-xl border border-surface-border p-6 shadow-card space-y-5">
                <h3 className="font-heading text-base font-bold text-text">
                  Booking Details
                </h3>

                {bookingDetails.cancellationPolicy && (
                  <div>
                    <p className="font-heading text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1">
                      Cancellation
                    </p>
                    <p className="font-body text-sm text-text-muted">
                      {bookingDetails.cancellationPolicy.description}
                    </p>
                  </div>
                )}
                {bookingDetails.mileage && (
                  <div>
                    <p className="font-heading text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1">
                      Mileage
                    </p>
                    <p className="font-body text-sm text-text-muted">
                      {bookingDetails.mileage}
                    </p>
                  </div>
                )}
                {bookingDetails.fuelPolicy && (
                  <div>
                    <p className="font-heading text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1">
                      Fuel Policy
                    </p>
                    <p className="font-body text-sm text-text-muted">
                      {bookingDetails.fuelPolicy}
                    </p>
                  </div>
                )}
                {bookingDetails.deposit?.methods && (
                  <div>
                    <p className="font-heading text-[10px] font-bold text-text-muted uppercase tracking-widest mb-2">
                      Accepted Payment
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {bookingDetails.deposit.methods.map((m) => (
                        <span
                          key={m}
                          className="font-body text-xs bg-surface-raised text-text-muted px-3 py-1 rounded-full border border-surface-border"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <aside className="lg:col-span-4 space-y-4 lg:sticky lg:top-24">
            {/* Location logistics */}
            <div className="bg-dark rounded-xl overflow-hidden">
              <div className="p-6">
                <h3 className="font-heading text-[10px] font-bold uppercase tracking-[0.2em] text-primary/70 mb-6 flex items-center gap-2">
                  <Plane size={12} /> Location Logistics
                </h3>

                <div className="space-y-6">
                  <div className="relative pl-5 border-l border-primary/20">
                    <div className="absolute -left-[5px] top-0 w-2.5 h-2.5 rounded-full bg-primary" />
                    <p className="font-heading text-sm font-semibold text-white mb-1.5">
                      Arrival Instructions
                    </p>
                    <p className="font-body text-xs text-white/60 leading-relaxed">
                      {location?.arrivalInstructions?.directions}
                    </p>
                  </div>

                  <div className="relative pl-5 border-l border-white/10">
                    <div className="absolute -left-[5px] top-0 w-2.5 h-2.5 rounded-full bg-dark-light" />
                    <p className="font-heading text-sm font-semibold text-white mb-3">
                      Operating Hours
                    </p>
                    <div className="space-y-2 font-body text-xs text-white/60">
                      <div className="flex justify-between">
                        <span>Mon – Fri</span>
                        <span className="text-white font-medium">
                          {location?.operatingHours?.monday}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sat – Sun</span>
                        <span className="text-white font-medium">
                          {location?.operatingHours?.saturday}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {location?.phone && (
                  <a
                    href={`tel:${location.phone}`}
                    className="w-full mt-6 py-3 flex items-center justify-center gap-2 bg-primary/10 hover:bg-primary text-primary hover:text-white font-heading text-[10px] font-bold uppercase tracking-widest rounded-lg transition-colors duration-200"
                  >
                    <Phone size={13} /> Call Branch
                  </a>
                )}
              </div>
            </div>

            {/* Help centre */}
            <a
              href="/support"
              className="bg-surface rounded-xl border border-surface-border p-5 flex items-center gap-4 shadow-card hover:shadow-primary-glow transition-shadow"
            >
              <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center text-accent-foreground flex-shrink-0">
                <Info size={18} />
              </div>
              <div>
                <p className="font-heading text-[10px] font-bold text-text-muted uppercase tracking-widest">
                  Need Help?
                </p>
                <p className="font-heading text-sm font-semibold text-text">
                  View Help Center
                </p>
              </div>
              <ChevronRight size={16} className="ml-auto text-surface-border" />
            </a>
          </aside>
        </div>
      </div>
    </div>
  );
}
